import React, { forwardRef, useImperativeHandle, useState } from "react";
import Select from "react-select";
import { Button, FormCheck, Modal } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";

import DFormText from "../../components/DFormText/DFormText";
import InputMask from "react-input-mask";

import api from "../../../services/api";
import ModalLoading from "../../components/ModalLoading/ModalLoading";
import { minutesToTime } from "../../../services/utils";

const ProcedureForm = forwardRef((props, ref) => {
    const { onClose } = props;
    const [modalBox, setModalBox] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [loadingProcedureForm, setLoadingProcedureForm] = useState(false);

    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [duration, setDuration] = useState("01:00");
    const [followUpForm, setFollowUpForm] = useState("");
    const [followUpFormOption, setFollowUpFormOption] = useState({value: "", label: ""});
    const [active, setActive] = useState(true);

    const [procedureId, setProcedureId] = useState(undefined);

    const [invalidCode, setInvalidCode] = useState(false);
    const [invalidName, setInvalidName] = useState(false);

    const [errorMessage, setErrorMessage] = useState("Ocorreu um problema ao salvar o procedimento");

    const formOptions = [
        {value: "", label: ""}, 
        {value: "EV/TVS", label: "EV/TVS"},
        {value: "MP/CDI", label: "MP/CDI"},
        {value: "FA", label: "FA"}
    ]

    function setOption (selectedOption) {
        setFollowUpForm(selectedOption.value)
        setFollowUpFormOption(selectedOption)
    }

    useImperativeHandle(ref, () => ({
        openModal(mode, procedureInfo = {}) {
            setModalBox(true);
            setIsUpdate(false);

            resetFields();

            if (mode === "update") {
                setIsUpdate(true);

                setProcedureId(procedureInfo._id);
                setCode(procedureInfo.code);
                setName(procedureInfo.name);
                setOption({value: procedureInfo.followUpForm, label: procedureInfo.followUpForm})
                setDuration(minutesToTime(procedureInfo.duration));
                setActive(procedureInfo.status);
            }
        },
    }));

    async function handleSaveProcedure(e) {
        e.preventDefault();

        setLoadingProcedureForm(true);

        setInvalidCode(false);
        setInvalidName(false);

        let hasError = false;

        if (code === "") {
            setInvalidCode(true);
            hasError = true;
        }

        if (name === "") {
            setInvalidName(true);
            hasError = true;
        }

        if (!hasError) {
            let durationInMinutes = 0;
            if (duration !== "") {
                var durationArray = duration.split(":");
                durationInMinutes = (+durationArray[0]) * 60 + (+durationArray[1]);
            }
            let procedure = {
                code,
                name,
                followUpForm,
                duration: +durationInMinutes,
                status: active
            };

            if (!isUpdate) {
                try {
                    await api.post("/procedures", procedure)
                        .then(response => {
                            if (response.status === 201) {
                                setLoadingProcedureForm(false);
                                notifySuccess();
                                resetFields();
                                onClose();
                                setModalBox(false);
                            }
                        })
                } catch (err) {
                    if (err.data?.error === "ALREADY_EXISTS") {
                        setErrorMessage("Procedimento já cadastrado");
                    }
                    notifyError();
                }
            } else {
                try {
                    await api.put(`/procedures/${procedureId}`, procedure)
                        .then(response => {
                            if (response.status === 200) {
                                setLoadingProcedureForm(false);
                                notifySuccess();
                                resetFields();
                                onClose();
                                setModalBox(false);
                            }
                        })
                } catch (err) {
                    notifyError();
                }
            }
        }

        setLoadingProcedureForm(false);
    }

    function notifySuccess() {
        toast.success(`✔️ Procedimento ${(isUpdate ? "alterado" : "incluído")} com sucesso!`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    }

    function notifyError() {
        toast.error(`❌ ${errorMessage}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    }

    function resetFields() {
        setProcedureId(undefined);
        setCode("");
        setName("");
        setFollowUpFormOption({})
        setDuration("01:00");
        setActive(true);

        setInvalidCode(false);
        setInvalidName(false);
    }

    return (
        <>
            <Modal size="xl" onHide={setModalBox} show={modalBox}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h3
                            className="modal-title"
                            id="exampleModalLabel"
                        >
                            {(!isUpdate ? "Novo " : "Alterar ")}Procedimento
                        </h3>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setModalBox(false)}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className="basic-form">
                            <div className="row" hidden={!isUpdate}>
                                <div className="form-group mb-3 col-md-6">
                                    <FormCheck
                                        type="switch"
                                        id="active"
                                        label="Ativo"
                                        defaultChecked={active}
                                        onChange={() => setActive(!active)}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group mb-3 col-md-2">
                                    <label>Código</label>
                                    <input
                                        type="text"
                                        className={`form-control ${(invalidCode ? "is-invalid" : "")}`}
                                        value={code}
                                        onChange={e => setCode(e.target.value)}
                                        tabIndex="1"
                                        disabled={isUpdate}
                                    />
                                    <DFormText hidden={!invalidCode} color="danger">Informe o código do procedimento</DFormText>
                                </div>
                                <div className="form-group mb-3 col-md-8">
                                    <label>Nome</label>
                                    <input
                                        type="text"
                                        className={`form-control ${(invalidName ? "is-invalid" : "")}`}
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        tabIndex="2"
                                    />
                                    <DFormText hidden={!invalidName} color="danger">Informe o nome do procedimento</DFormText>
                                </div>
                                <div className="form-group mb-3 col-md-2">
                                    <label>Duração</label>
                                    <InputMask
                                        mask="99:99"
                                        maskChar=" "
                                        value={duration}
                                        onChange={(event) => {
                                            setDuration(event?.target.value);
                                        }}
                                        alwaysShowMask={true}
                                        tabIndex="3"
                                    >
                                        {(inputProps) => <input {...inputProps} type="text" className="form-control" tabIndex="3" />}
                                    </InputMask>
                                </div>
                                <div className="form-group mb-3 col-md-2">
                                    <label>Formulário seguimento</label>
                                    <Select
                                        id="followUpForm"
                                        defaultValue={followUpFormOption}
                                        value={followUpFormOption}
                                        onChange={e => setOption(e)}
                                        options={formOptions}
                                        styles={{
                                            control: (baseStyles, state) => ({
                                                ...baseStyles,
                                                height: 45,
                                            }),
                                        }}
                                        placeholder="Selecione..."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <Button
                                variant="outline-danger btn-rounded"
                                onClick={() => setModalBox(false)}
                            >
                                Fechar
                            </Button>
                            <Button
                                variant="success btn-rounded"
                                onClick={handleSaveProcedure}
                            >
                                Salvar
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal >

            <ToastContainer />

            <ModalLoading
                visible={loadingProcedureForm}
                message="Salvando Procedimento..."
                onClose={setLoadingProcedureForm}
            />
        </>
    );
});

export default ProcedureForm;
